import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["prevButton", "nextButton", "scrollContainer"]
  static values = {
    totalColumns: Number,
    currentPage: { type: Number, default: 0 },
    minColumnWidth: { type: Number, default: 140 },
    debug: { type: Boolean, default: false }
  }

  connect() {
    this.setupScrollContainer()
    this.updateView()

    // Ajouter l'écoute des touches fléchées
    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.handleKeydown)

    // Ajouter l'écoute du scroll horizontal avec la molette
    this.handleWheel = this.handleWheel.bind(this)
    // Bind la fonction updateButtons une seule fois pour pouvoir la supprimer correctement
    this.boundUpdateButtons = this.updateButtons.bind(this)

    if (this.hasScrollContainerTarget) {
      this.scrollContainerTarget.addEventListener('wheel', this.handleWheel, { passive: false })
      this.scrollContainerTarget.addEventListener('scroll', this.boundUpdateButtons)
    }
  }

  disconnect() {
    // Nettoyer l'écoute des touches fléchées
    document.removeEventListener('keydown', this.handleKeydown)

    // Nettoyer l'écoute du scroll
    if (this.hasScrollContainerTarget) {
      this.scrollContainerTarget.removeEventListener('wheel', this.handleWheel)
      this.scrollContainerTarget.removeEventListener('scroll', this.boundUpdateButtons)
    }

    // Nettoyer le ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
  }

  handleWheel(event) {
    // Permettre le scroll vertical normal, seulement intercepter le scroll horizontal
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
      // Laisser le scroll horizontal naturel se faire
      return
    }
    // Le scroll vertical normal continue
  }

  setupScrollContainer() {
    if (!this.hasScrollContainerTarget) return

    // Configurer la largeur totale basée sur le nombre de colonnes et la largeur minimale
    const totalWidth = this.totalColumnsValue * this.minColumnWidthValue
    const containerWidth = this.scrollContainerTarget.offsetWidth

    // Si le contenu dépasse la largeur du conteneur, activer le scroll
    if (totalWidth > containerWidth) {
      this.scrollContainerTarget.style.overflowX = 'auto'
      this.scrollContainerTarget.style.scrollBehavior = 'smooth'
    } else {
      this.scrollContainerTarget.style.overflowX = 'hidden'
    }

    // Observer les changements dans le contenu pour mettre à jour les boutons
    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        // Délai pour laisser le temps au DOM de se stabiliser
        setTimeout(() => this.updateButtons(), 100)
      })
      this.resizeObserver.observe(this.scrollContainerTarget)
    }
  }

  handleKeydown(event) {
    // Ignorer si l'utilisateur tape dans un champ de saisie
    if (event.target.matches('input, textarea, select, [contenteditable]')) {
      return
    }

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault()
        this.scrollToPreviousPage()
        break
      case 'ArrowRight':
        event.preventDefault()
        this.scrollToNextPage()
        break
    }
  }

  scrollToPreviousPage() {
    if (!this.hasScrollContainerTarget) return

    const scrollAmount = this.minColumnWidthValue
    this.scrollContainerTarget.scrollLeft -= scrollAmount

    // Forcer la mise à jour des boutons après le scroll
    setTimeout(() => this.updateButtons(), 50)
  }

  scrollToNextPage() {
    if (!this.hasScrollContainerTarget) return

    const scrollAmount = this.minColumnWidthValue
    this.scrollContainerTarget.scrollLeft += scrollAmount

    // Forcer la mise à jour des boutons après le scroll
    setTimeout(() => this.updateButtons(), 50)
  }

  currentPageValueChanged() {
    this.updateView()
  }

  previous() {
    this.scrollToPreviousPage()
  }

  next() {
    this.scrollToNextPage()
  }

  updateView() {
    this.updateColumnWidths()
    this.updateButtons()
  }

  updateColumnWidths() {
    if (!this.hasScrollContainerTarget) return

    // Définir la largeur minimale pour toutes les colonnes
    const headerColumns = this.element.querySelectorAll('[data-bookable-column]')
    const contentColumns = this.element.querySelectorAll('[data-bookable-line]')

    headerColumns.forEach(col => {
      col.style.minWidth = `${this.minColumnWidthValue}px`
      col.style.width = `${this.minColumnWidthValue}px`
    })

    contentColumns.forEach(col => {
      col.style.minWidth = `${this.minColumnWidthValue}px`
      col.style.width = `${this.minColumnWidthValue}px`
    })
  }

  updateButtons() {
    if (!this.hasScrollContainerTarget) return

    const scrollLeft = this.scrollContainerTarget.scrollLeft
    const scrollWidth = this.scrollContainerTarget.scrollWidth
    const clientWidth = this.scrollContainerTarget.clientWidth

    // Ajouter une petite tolérance pour éviter les problèmes de précision des nombres flottants
    const tolerance = 1

    // Bouton précédent - désactivé si on est au début
    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.disabled = scrollLeft <= tolerance
    }

    // Bouton suivant - désactivé si on est à la fin
    if (this.hasNextButtonTarget) {
      const maxScrollLeft = scrollWidth - clientWidth
      this.nextButtonTarget.disabled = scrollLeft >= (maxScrollLeft - tolerance)
    }

    // Debug pour comprendre les valeurs (peut être supprimé plus tard)
    if (this.debugValue) {
      console.log('updateButtons:', {
        scrollLeft,
        scrollWidth,
        clientWidth,
        maxScrollLeft: scrollWidth - clientWidth,
        prevDisabled: this.hasPrevButtonTarget ? this.prevButtonTarget.disabled : 'N/A',
        nextDisabled: this.hasNextButtonTarget ? this.nextButtonTarget.disabled : 'N/A'
      })
    }
  }
}
